import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SearchIcon } from '@hero/web-icons';
import {
  useAppsApi,
  useAppsInstalledApi,
  useAppEarlyAccessApi,
} from '@hero/api-hooks';
import type {
  AppView,
  AppCategory,
  AppStatus,
  AppInstalledView,
} from '@hero/api';
import {
  AppLogo,
  AppPromoLabelBadge,
  appPromoCardClassName,
  appPromoConnectButtonClassName,
  LogistiConnectModal,
} from '@hero/web-components';
import { LOGISTI_APP } from '@hero/apps';
import { useAppCatalogVisibleCheck, useAppConnectorLive, useAppConnectorLiveCheck } from '@hero/web-hooks';

type Filter = 'all' | AppCategory;

/**
 * The app whose install modal this page knows how to open.
 *
 * Lifecycle `active` / `beta` says whether Connect is offered; this id says
 * which modal to render. Every installable app needs its own credential form.
 */
const LOGISTI_APP_ID = LOGISTI_APP.id;

const STATUS_PILL: Record<AppStatus, string> = {
  active: 'bg-green-100 text-green-700',
  beta: 'bg-violet-100 text-violet-700',
  soon: 'bg-gray-100 text-gray-600',
  inactive: 'bg-gray-100 text-gray-600',
};

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'border-primary bg-primary text-white'
          : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:text-primary'
      }`}>
      {label}
    </button>
  );
}

function AppCard({
  app,
  onApply,
  isApplying,
  onConnect,
  install,
}: {
  app: AppView;
  onApply: (app: AppView) => void;
  isApplying: boolean;
  /** Present only for apps with a live connector. */
  onConnect?: () => void;
  install?: AppInstalledView;
}) {
  const { t } = useTranslation(['a-carrier-dashboard', 'm-apps']);
  const isConnected = Boolean(install?.enabled);
  // A row that exists but is switched off. The distinction is worth a word of
  // its own: "not connected" reads as never set up, and would send someone who
  // deliberately turned this off back through the whole setup.
  const isTurnedOff = Boolean(install) && !isConnected;

  const cardBadge = (() => {
    if (onConnect) {
      if (isConnected) {
        return (
          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
            {t('m-apps:logisti.connected')}
          </span>
        );
      }
      if (isTurnedOff) {
        return (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">
            {t('m-apps:logisti.turned_off')}
          </span>
        );
      }
      if (app.label) {
        return <AppPromoLabelBadge label={app.label} size="md" />;
      }
      return (
        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600">
          {t('m-apps:logisti.disconnected')}
        </span>
      );
    }
    if (app.label) {
      return <AppPromoLabelBadge label={app.label} size="md" />;
    }
    return (
      <span
        className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[app.status]}`}>
        {t(`a-carrier-dashboard:apps_page.status.${app.status}`)}
      </span>
    );
  })();

  return (
    <div
      className={appPromoCardClassName(app.label, {
        connected: onConnect ? isConnected : false,
      })}>
      <div className="flex items-start justify-between gap-2">
        <AppLogo name={app.name} logo={app.logo} className="size-12 shrink-0" />
        <div className="shrink-0">{cardBadge}</div>
      </div>

      <div className="flex flex-1 flex-col gap-1">
        <h3 className="text-sm font-semibold text-gray-900">{app.name}</h3>
        <p className="text-xs leading-relaxed text-gray-500">{app.description}</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {app.categories.map(category => (
          <span
            key={category}
            className="rounded-md bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-500">
            {t(`a-carrier-dashboard:apps_page.filters.${category}`)}
          </span>
        ))}
      </div>

      {onConnect ? (
        <button
          type="button"
          onClick={onConnect}
          className={appPromoConnectButtonClassName(app.label, { connected: isConnected })}>
          {isConnected
            ? t('m-apps:logisti.manage')
            : isTurnedOff
              ? t('m-apps:logisti.reconnect')
              : t('m-apps:logisti.connect')}
        </button>
      ) : (
        <button
          type="button"
          disabled={isApplying}
          onClick={() => onApply(app)}
          className={appPromoConnectButtonClassName(app.label, { disabled: isApplying })}>
          {t('a-carrier-dashboard:apps_page.request_access')}
        </button>
      )}
    </div>
  );
}

export default function AppsGrid() {
  const { t } = useTranslation(['a-carrier-dashboard']);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const [isLogistiModalOpen, setLogistiModalOpen] = useState(false);

  const { data: apps = [], isPending } = useAppsApi();
  const { data: installs = [] } = useAppsInstalledApi();

  const isAppVisible = useAppCatalogVisibleCheck();
  const isAppLive = useAppConnectorLiveCheck();
  const isLogistiLive = useAppConnectorLive(LOGISTI_APP_ID);

  const logistiInstall = installs.find(item => item.appId === LOGISTI_APP_ID);

  const earlyAccess = useAppEarlyAccessApi({
    onSuccess: () => toast.success(t('a-carrier-dashboard:apps_page.request_success')),
    onError: () => toast.error(t('a-carrier-dashboard:apps_page.request_error')),
  });

  const visibleApps = useMemo(
    () => apps.filter(app => isAppVisible(app)),
    [apps, isAppVisible],
  );

  const categories = useMemo(() => {
    const seen = new Set<AppCategory>();
    visibleApps.forEach(app => app.categories.forEach(category => seen.add(category)));
    return Array.from(seen);
  }, [visibleApps]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return visibleApps.filter(app => {
      const matchesFilter = filter === 'all' || app.categories.includes(filter);
      const matchesSearch =
        !search ||
        app.name.toLowerCase().includes(search) ||
        app.description.toLowerCase().includes(search);
      return matchesFilter && matchesSearch;
    });
  }, [visibleApps, filter, query]);

  const handleApply = (app: AppView) => {
    earlyAccess.mutate({ appId: app.id, appName: app.name });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-gray-900">
          {t('a-carrier-dashboard:apps_page.title')}
        </h1>
        <p className="text-sm text-gray-500">{t('a-carrier-dashboard:apps_page.subtitle')}</p>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          <FilterChip
            active={filter === 'all'}
            label={t('a-carrier-dashboard:apps_page.filters.all')}
            onClick={() => setFilter('all')}
          />
          {categories.map(category => (
            <FilterChip
              key={category}
              active={filter === category}
              label={t(`a-carrier-dashboard:apps_page.filters.${category}`)}
              onClick={() => setFilter(category)}
            />
          ))}
        </div>

        <div className="relative lg:w-72">
          <SearchIcon className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t('a-carrier-dashboard:apps_page.search_placeholder')}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 ps-9 pe-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      {isPending ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-500">
          {t('a-carrier-dashboard:apps_page.loading')}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {filtered.map(app => (
            <AppCard
              key={app.id}
              app={app}
              onApply={handleApply}
              isApplying={earlyAccess.isPending}
              onConnect={
                isAppLive(app) && app.id === LOGISTI_APP_ID
                  ? () => setLogistiModalOpen(true)
                  : undefined
              }
              install={
                isAppLive(app) && app.id === LOGISTI_APP_ID ? logistiInstall : undefined
              }
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-500">
          {t('a-carrier-dashboard:apps_page.empty')}
        </div>
      )}

      {isLogistiLive ? (
        <LogistiConnectModal
          isOpen={isLogistiModalOpen}
          onClose={() => setLogistiModalOpen(false)}
          install={logistiInstall}
        />
      ) : null}
    </div>
  );
}
